import { composeInitialState } from '@opengov/compose-app';
import { EmptyListOf } from '@opengov/compose-app/immutable';
import { List } from 'immutable';
import { IOgqlView, IQueryState } from '@opengov/ogql-types';
import { EQueryType } from '@opengov/reporting-types';

import { composeStateMachine } from './module';
import { OgqlView, QueryState } from './records';

export const ogqlInitialState = composeInitialState({
  queries: EmptyListOf(QueryState),
  loadingView: false,
  loadingViewList: false,
  views: EmptyListOf(OgqlView)
});

export const shouldNotCancelQuery = (
  query: IQueryState,
  queryType: IQueryState['queryType'],
  queryParams: IQueryState['queryParams'] = {}
): boolean => {
  const { allowQueryExecution = false } = queryParams || {};
  // Child queries are for a different row level than parent queries — a new parent
  // query should not cancel in-flight child queries (they serve different stores).
  // Only cancel a child query when another child query for a different parent is queued.
  if (query.queryType === EQueryType.detailTableGroupChildQuery) {
    if (queryType !== EQueryType.detailTableGroupChildQuery) {
      return true;
    }
    return query.queryParams.childParentKey === queryParams.childParentKey;
  }

  const specialCases = [
    EQueryType.pivotRows,
    EQueryType.pivotChildRows,
    EQueryType.dimensionalSummaryTableGroupData,
    EQueryType.dimensionalSummaryTableChildData
  ];

  if (
    query.queryType === queryType &&
    specialCases.includes(queryType) &&
    !isNaN(queryParams.dimensionalSummaryQueryIndex) &&
    query.queryParams &&
    !isNaN(query.queryParams.dimensionalSummaryQueryIndex)
  ) {
    return (
      allowQueryExecution || query.queryParams.dimensionalSummaryQueryIndex !== queryParams.dimensionalSummaryQueryIndex
    );
  } else {
    return query.queryType !== queryType;
  }
};

// These actions track ogql queries as they are requested, started, polled, and complete/fail
export const ogqlState = composeStateMachine('ogql-actions', ogqlInitialState, (compose) => ({
  // Queue up a query to be executed and tracked
  // also cancels any other queries of the same type
  queueQuery: compose.action((state, params: IQueryState) =>
    state
      // remove any other query of the same type
      // but allow various things through
      .update('queries', (qs) => qs.filter((q) => shouldNotCancelQuery(q, params.queryType, params.queryParams)))
      .update('queries', (qs) => qs.push(QueryState(params)))
  ),

  loadingViews: compose.payloadlessAction((state) => state.set('loadingViewList', true)),

  loadedViews: compose.action((state, params: { views: IOgqlView[] }) =>
    state.set('loadingViewList', false).set('views', List(params.views.map(OgqlView)))
  ),
  // Cancel all queries of a certain requestId -
  // used to cancel queries for a report visualization or table
  // as users change their filter selections
  cancelQueriesOfType: compose.action((state, queryType: string) =>
    state.update('queries', (qs) => qs.filter((q) => q.queryType !== queryType))
  ),

  queryFinished: compose.action((state, params: IQueryState) =>
    state.update('queries', (qs) => qs.filter((q) => q.id !== params.id))
  ),

  queryFailed: compose.action((state, params: IQueryState) =>
    state.update('queries', (qs) => qs.filter((q) => q.id !== params.id))
  )
}));

export const {
  actions: ogqlClientActions,
  actionCreators: ogqlClientActionCreators,
  reducer: ogqlClientReducer,
  selector: ogqlClientSelector
} = ogqlState;
